import { useState, FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'

type Permission = {
  id: number
  code: string
  name: string
  category: string
  description: string
  is_system: boolean
}

type Role = {
  id: number
  name: string
  description: string
  is_system: boolean
  permission_codes: string[]
  user_count: number
}

type RoleInput = {
  name: string
  description: string
  permission_ids: number[]
}

const emptyRole: RoleInput = { name: '', description: '', permission_ids: [] }

export default function RolesList() {
  const qc = useQueryClient()

  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: async () => (await api.get<Role[]>('/roles/')).data,
  })

  const permsQ = useQuery({
    queryKey: ['permissions'],
    queryFn: async () => (await api.get<Permission[]>('/permissions/')).data,
  })

  const [editing, setEditing] = useState<Role | null>(null)
  const [creating, setCreating] = useState(false)

  const createMut = useMutation({
    mutationFn: async (payload: RoleInput) => (await api.post('/roles/', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      setCreating(false)
    },
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: RoleInput }) =>
      (await api.patch(`/roles/${id}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      setEditing(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/roles/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
    },
  })

  const roles = rolesQ.data || []
  const perms = permsQ.data || []

  // group permissions by category for the picker
  const permsByCategory: Record<string, Permission[]> = {}
  perms.forEach((p) => {
    const k = p.category || 'Other'
    if (!permsByCategory[k]) permsByCategory[k] = []
    permsByCategory[k].push(p)
  })

  // map perm code → id (for converting back when editing)
  const codeToId: Record<string, number> = {}
  perms.forEach((p) => { codeToId[p.code] = p.id })

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Roles ({roles.length})</h2>
        <Button onClick={() => setCreating(true)}>+ Add role</Button>
      </div>

      {rolesQ.isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Name</Th>
                <Th>Description</Th>
                <Th>Permissions</Th>
                <Th>Users</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {roles.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <Td>
                    <span className="font-medium">{r.name}</span>
                    {r.is_system && (
                      <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">system</span>
                    )}
                  </Td>
                  <Td className="text-gray-600">{r.description || '—'}</Td>
                  <Td>
                    <span className="text-sm text-gray-700">{r.permission_codes.length}</span>
                  </Td>
                  <Td>
                    <span className="text-sm text-gray-700">{r.user_count}</span>
                  </Td>
                  <Td>
                    <button onClick={() => setEditing(r)} className="text-blue-600 hover:underline text-sm mr-3">
                      Edit
                    </button>
                    {!r.is_system && (
                      <button
                        onClick={() => {
                          if (confirm(`Delete role "${r.name}"?`)) deleteMut.mutate(r.id)
                        }}
                        className="text-red-600 hover:underline text-sm"
                      >
                        Delete
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(creating || editing) && (
        <RoleDialog
          initial={
            editing
              ? {
                  name: editing.name,
                  description: editing.description,
                  permission_ids: editing.permission_codes.map((c) => codeToId[c]).filter(Boolean),
                }
              : emptyRole
          }
          isEdit={!!editing}
          permsByCategory={permsByCategory}
          submitting={createMut.isPending || updateMut.isPending}
          error={(createMut.error as any) || (updateMut.error as any)}
          onCancel={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSubmit={(payload) => {
            if (editing) updateMut.mutate({ id: editing.id, payload })
            else createMut.mutate(payload)
          }}
        />
      )}
    </AdminLayout>
  )
}

function RoleDialog({
  initial,
  isEdit,
  permsByCategory,
  onSubmit,
  onCancel,
  submitting,
  error,
}: {
  initial: RoleInput
  isEdit: boolean
  permsByCategory: Record<string, Permission[]>
  onSubmit: (payload: RoleInput) => void
  onCancel: () => void
  submitting: boolean
  error: any
}) {
  const [form, setForm] = useState<RoleInput>(initial)
  const togglePerm = (id: number) => {
    setForm((f) => ({
      ...f,
      permission_ids: f.permission_ids.includes(id)
        ? f.permission_ids.filter((x) => x !== id)
        : [...f.permission_ids, id],
    }))
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit(form)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">{isEdit ? 'Edit role' : 'Add role'}</h3>
        <form onSubmit={submit}>
          <FieldSet>
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="role-name">Name</FieldLabel>
                <Input
                  id="role-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="role-description">Description</FieldLabel>
                <Textarea
                  id="role-description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                />
              </Field>

              <Field>
                <FieldLabel>Permissions</FieldLabel>
                <div className="border border-gray-200 rounded-md max-h-72 overflow-y-auto">
                  {Object.entries(permsByCategory).map(([cat, perms]) => (
                    <div key={cat} className="border-b border-gray-100 last:border-b-0">
                      <div className="px-3 py-1.5 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                        {cat}
                      </div>
                      <div className="p-2 space-y-1">
                        {perms.map((p) => (
                          <label key={p.id} className="flex items-start space-x-2 px-2 py-1 hover:bg-gray-50 rounded">
                            <input
                              type="checkbox"
                              checked={form.permission_ids.includes(p.id)}
                              onChange={() => togglePerm(p.id)}
                              className="mt-0.5"
                            />
                            <div>
                              <div className="text-sm text-gray-700">{p.name}</div>
                              <div className="text-xs text-gray-400 font-mono">{p.code}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Field>
            </FieldGroup>
          </FieldSet>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded mt-3">
              {String(error?.response?.data ? JSON.stringify(error.response.data) : error.message || error)}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{children}</th>
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm ${className || ''}`}>{children}</td>
}
